import { AdminDeletionTarget } from '@cpce/shared';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useAdminDelete, useAdminDeletionPreview } from '@/api/admin-deletions/admin-deletions';
import { extractErrorMessage } from '@/api/mutations.utils';
import { queryClient } from '@/api/queries.utils';
import { useAuth } from '@/context/useAuth';
import { Button } from './Button';
import { Modal } from './Modal';
import { Skeleton } from './Skeleton';

interface AdminDeleteButtonProps {
  target: AdminDeletionTarget;
  id: number;
  // Called once the rows are gone, before the cache is invalidated: navigate away from a detail
  // page here, or it refetches the deleted object and flashes a « not found ».
  onDeleted?: () => void;
}

const PREFERRED_ALTERNATIVE: Record<AdminDeletionTarget, string> = {
  [AdminDeletionTarget.prestation]:
    'Si la prestation a déjà servi, préférez « Archiver » : elle disparaît des listes et sa comptabilité reste intacte.',
  [AdminDeletionTarget.prestationParticipation]:
    'Préférez « Annuler le dossier » : la contre-passation garde la trace comptable et la numérotation des écritures.',
  [AdminDeletionTarget.transaction]:
    "Préférez « Annuler l'écriture » : la contre-passation garde la trace comptable et la numérotation des écritures.",
  [AdminDeletionTarget.prestationTransaction]:
    'Préférez « Annuler le règlement » : la contre-passation garde la trace comptable et la numérotation des écritures.',
  [AdminDeletionTarget.supplierInvoice]:
    'Pour revenir sur une facture validée, préférez un avoir fournisseur : la facture et ses écritures restent en place.',
  [AdminDeletionTarget.supplierPayment]:
    'Préférez « Annuler » le règlement : la contre-passation garde la trace comptable et la numérotation des écritures.',
};

export function AdminDeleteButton(props: AdminDeleteButtonProps) {
  const { target, id, onDeleted } = props;
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const preview = useAdminDeletionPreview(target, id, open);
  const { mutateAsync: hardDelete, isPending } = useAdminDelete(target, id);

  if (!isAdmin) return null;

  return (
    <>
      <Button variant="outline" color="red" icon={Trash2} onClick={() => setOpen(true)}>
        Supprimer définitivement
      </Button>
      <Modal title="Suppression définitive" open={open} onOpenChange={setOpen}>
        <div className="flex flex-col gap-4">
          <p>
            Action réservée aux administrateurs Pass'CE. Les données ci-dessous seront effacées sans
            contre-passation, y compris sur un exercice clôturé. Cette action est irréversible.
          </p>
          <p className="rounded-md bg-gray-50 p-3 text-gray-700">{PREFERRED_ALTERNATIVE[target]}</p>
          {preview.isLoading && <Skeleton className="h-16 w-full" />}
          {preview.error && (
            <p className="rounded-md bg-red-50 p-3 text-red-700">
              Suppression impossible : {extractErrorMessage(preview.error)}
            </p>
          )}
          {preview.data && (
            <>
              <ul className="list-disc pl-6">
                {preview.data.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {preview.data.warnings.length > 0 && (
                <ul className="flex flex-col gap-1 rounded-md bg-orange-50 p-3 text-orange-800">
                  {preview.data.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}
            </>
          )}
          <div className="flex gap-2 justify-between">
            <Button
              variant="outline"
              className="grow"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Annuler
            </Button>
            <Button
              variant="color"
              color="red"
              className="grow"
              disabled={!preview.data}
              isPending={isPending}
              onClick={async () => {
                await hardDelete();
                setOpen(false);
                onDeleted?.();
                await queryClient.invalidateQueries();
              }}
            >
              Supprimer définitivement
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
